import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart3, Handshake, Loader2, ArrowRight, Clock } from 'lucide-react';
import api from '../../utils/api';
import { FASES_ORDEN, FASES_DEGRADADO, getFaseColor } from './constants';

const ESTADO_COMPLETO_POR_FASE = {
  'Liberación': [],
  'Inspección': ['inspeccion_terminada'],
  'Programación': ['fecha_confirmada', 'citacion_notarial'],
  'Entrega': ['entrega_aceptada_sin_obs', 'entrega_aceptada_con_obs_cliente'],
  'Levantamiento': ['observaciones_subsanadas'],
  'Cierre': ['entrega_terminada']
};

const calcularProgreso = (fase, data) => {
  if (!data || data.total === 0) return 0;
  const estadosCompletos = ESTADO_COMPLETO_POR_FASE[fase] || [];
  if (fase === 'Liberación') {
    const sinAsignar = data.estados?.['sin_arquitecto_asignado'] || 0;
    return Math.round(((data.total - sinAsignar) / data.total) * 100);
  }
  const completados = estadosCompletos.reduce(
    (sum, estado) => sum + (data.estados?.[estado] || 0), 0
  );
  return Math.round((completados / data.total) * 100);
};

const EntregaDashboard = () => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({});
  const [estados, setEstados] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [arquitectos, setArquitectos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroProyecto, setFiltroProyecto] = useState('all');
  const [filtroEtapa, setFiltroEtapa] = useState('all');
  const [filtroArquitecto, setFiltroArquitecto] = useState('all');

  const catalogosCargados = useRef(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroProyecto && filtroProyecto !== 'all') params.append('proyecto_id', filtroProyecto);
      if (filtroEtapa && filtroEtapa !== 'all') params.append('subdivision_id', filtroEtapa);
      if (filtroArquitecto && filtroArquitecto !== 'all') params.append('arquitecto_id', filtroArquitecto);

      if (!catalogosCargados.current) {
        // Primera carga: pipeline + estados + arquitectos en una sola petición, más proyectos
        const [resumenResp, proyResp] = await Promise.all([
          api.get(`/entregas/dashboard/resumen?${params.toString()}`),
          api.get('/proyectos')
        ]);
        setPipeline(resumenResp.data?.pipeline || {});
        setEstados(resumenResp.data?.estados || []);
        setArquitectos(resumenResp.data?.arquitectos || []);
        setProyectos(proyResp.data || []);
        catalogosCargados.current = true;
      } else {
        // Cambio de filtros: solo se recalcula el pipeline
        const pipeResp = await api.get(`/entregas/dashboard/pipeline?${params.toString()}`);
        setPipeline(pipeResp.data || {});
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [filtroProyecto, filtroEtapa, filtroArquitecto]);

  useEffect(() => {
    const loadEtapas = async () => {
      if (filtroProyecto && filtroProyecto !== 'all') {
        try {
          const resp = await api.get(`/proyectos/${filtroProyecto}/subdivisiones`);
          const subs = resp.data || [];
          setEtapas(subs.filter(s => s.tipo === 'etapa' || !s.padre_id));
        } catch {
          setEtapas([]);
        }
      } else {
        setEtapas([]);
        setFiltroEtapa('all');
      }
    };
    loadEtapas();
  }, [filtroProyecto]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalEntregas = Object.values(pipeline).reduce((sum, fase) => sum + fase.total, 0);
  const ahora = new Date().toLocaleString('es-PE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const navigateToEntregas = (fase, estado) => {
    const filtros = { filtroFase: fase, filtroEstado: estado || 'all', page: 1 };
    localStorage.setItem('entregas_filtros', JSON.stringify(filtros));
    navigate('/entregas');
  };

  return (
    <div className="space-y-5" data-testid="entregas-dashboard">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BarChart3 size={22} className="text-[#4E7A79]" />
        <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D]">Dashboard Entregas</h1>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Select value={filtroProyecto} onValueChange={(v) => { setFiltroProyecto(v); setFiltroEtapa('all'); }}>
          <SelectTrigger className="text-sm bg-white" data-testid="dashboard-filtro-proyecto">
            <SelectValue placeholder="Proyecto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los proyectos</SelectItem>
            {proyectos.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtroEtapa} onValueChange={setFiltroEtapa} disabled={filtroProyecto === 'all'}>
          <SelectTrigger className="text-sm bg-white" data-testid="dashboard-filtro-etapa">
            <SelectValue placeholder="Etapa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las etapas</SelectItem>
            {etapas.map(e => (
              <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtroArquitecto} onValueChange={setFiltroArquitecto}>
          <SelectTrigger className="text-sm bg-white" data-testid="dashboard-filtro-arquitecto">
            <SelectValue placeholder="Arquitecto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los arquitectos</SelectItem>
            {arquitectos.map(u => (
              <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPIs por fase — degradé estilo Postventa */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {FASES_ORDEN.map(fase => {
          const data = pipeline[fase];
          const count = data?.total || 0;
          const grad = FASES_DEGRADADO[fase];
          return (
            <Card
              key={fase}
              className="cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 border-0 shadow-lg text-white h-full"
              style={{ background: `linear-gradient(to bottom right, ${grad.from}, ${grad.to})` }}
              onClick={() => navigateToEntregas(fase)}
              data-testid={`kpi-${fase}`}
            >
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold drop-shadow-sm">{count}</div>
                <div className="text-xs mt-1.5 opacity-85 font-medium truncate">{fase}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Total */}
      <Card className="border border-[#E8E8E7]">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Handshake size={18} className="text-[#4E7A79]" />
            <span className="text-sm font-medium text-[#2D2D2D]">Total de entregas</span>
          </div>
          <Badge className="text-white text-lg px-3" style={{ backgroundColor: '#4E7A79' }}>{totalEntregas}</Badge>
        </CardContent>
      </Card>

      {/* Separador: Pipeline de Unidades por Fase */}
      <div className="flex items-center gap-3 pt-1">
        <h2 className="text-base font-semibold text-[#2D2D2D] whitespace-nowrap">Pipeline de Unidades por Fase</h2>
        <div className="flex-1 h-px bg-[#E8E8E7]" />
        <span className="text-xs text-[#888] whitespace-nowrap">{totalEntregas} unidades</span>
        <div className="flex items-center gap-1 text-xs text-[#aaa]">
          <Clock size={12} />
          <span>{ahora}</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-[#4E7A79]" />
          <span className="ml-2 text-sm text-[#888]">Cargando pipeline...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
          {FASES_ORDEN.map((fase) => {
            const data = pipeline[fase];
            const color = getFaseColor(fase);
            const estadosDeFase = estados.filter(e => e.fase === fase);
            const progreso = calcularProgreso(fase, data);

            return (
              <Card key={fase} className="border-0 shadow-sm overflow-hidden flex flex-col" data-testid={`pipeline-${fase}`}>
                {/* Header con fondo sólido */}
                <div
                  className="px-4 py-3 flex items-center justify-between"
                  style={{ backgroundColor: color }}
                >
                  <span className="text-sm font-semibold text-white">{fase}</span>
                  <Badge className="bg-white/20 text-white border-0 text-xs font-bold">
                    {data?.total || 0}
                  </Badge>
                </div>

                {/* Estados — crece para empujar progress bar al fondo */}
                <CardContent className="p-3 space-y-1.5 flex-1">
                  {estadosDeFase.map(estado => {
                    const count = data?.estados?.[estado.estado] || 0;
                    return (
                      <div
                        key={estado.estado}
                        className="flex items-center justify-between py-2 px-3 rounded-md bg-[#F7F7F6] cursor-pointer transition-all duration-150 hover:shadow-sm group"
                        style={{ borderLeft: '3px solid transparent' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderLeftColor = color;
                          e.currentTarget.style.backgroundColor = `${color}08`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderLeftColor = 'transparent';
                          e.currentTarget.style.backgroundColor = '#F7F7F6';
                        }}
                        onClick={() => navigateToEntregas(fase, estado.estado)}
                        data-testid={`estado-${estado.estado}`}
                      >
                        <span className="text-xs text-[#5A5A5A] group-hover:text-[#2D2D2D] transition-colors font-medium">
                          {estado.estado_label}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span
                            className="text-xs font-bold min-w-[20px] text-center"
                            style={{ color: count > 0 ? color : '#bbb' }}
                          >
                            {count}
                          </span>
                          <ArrowRight size={12} className="text-[#ddd] group-hover:text-[#888] transition-colors" />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>

                {/* Progress bar — siempre al fondo, alineado entre cards */}
                <div className="px-3 pb-3 pt-0">
                  <div className="border-t border-[#EEEEEE] pt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[#aaa] font-medium uppercase tracking-wider">Avance</span>
                      <span className="text-[10px] font-bold" style={{ color }}>{progreso}%</span>
                    </div>
                    <div className="w-full h-2 bg-[#EEEEEE] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${progreso}%`,
                          backgroundColor: color,
                          minWidth: progreso > 0 ? '8px' : '0'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EntregaDashboard;
